import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { BOM_PACKAGING } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { getStatusColor } from '../../components/StatusSelect';
import { SelectDropdown } from '../../components/SelectDropdown';
import FilterDropdown from '../../components/FilterDropdown';
import { ACTIVE, IN_ACTIVE, SUNSET, PLANNED, PHASED_STATUSES, enumInventoryPhasedStatus } from '../../consts/inventoryStatus';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { BOM_PACKAGING_TYPE } from '../../consts/cronTypes';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';

const BOMPackaging = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['bom-packaging'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['bom-packaging'] || [];

  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportType, setReportType] = useState('product'); // 'product' or 'packaging'
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState([ACTIVE, IN_ACTIVE, SUNSET]);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const pageSize = 50;

  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  useEffect(() => {
    dispatch(getReportEmailSettings('bom-packaging'));
    dispatch(getReportSendHistory('bom-packaging'));
  }, [dispatch]);

  useEffect(() => {
    fetchReportMetadata();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(String(search || '').trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!initialReady) return;
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialReady, currentPage, reportType, debouncedSearch, selectedPhasedStatuses]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${BOM_PACKAGING}/metadata`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch report metadata');
      const data = await response.json();
      // Update columns based on report type
      updateColumnsForReportType(data.columns || [], reportType);
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const updateColumnsForReportType = (baseColumns, type) => {
    if (type === 'packaging') {
      // Remove component_type column for packaging
      const filteredColumns = baseColumns.filter(col => col.key !== 'component_type');
      setColumns(filteredColumns);
    } else {
      // Include all columns for product and all (product + packaging)
      setColumns(baseColumns);
    }
  };

  const fetchReportData = async () => {
    const { signal, requestId } = beginRequest();

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        reportType: reportType,
      });
      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
        params.append('phasedStatuses', selectedPhasedStatuses.join(','));
      }

      const response = await fetch(`/api/v1/reports/${BOM_PACKAGING}?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch report data');
      }

      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      setTotalRecords(data.totalRecords || 0);
      setTotalPages(data.totalPages || 1);
      // Update columns based on report type
      if (data.columns) {
        updateColumnsForReportType(data.columns, reportType);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth ?? null,
      slackChannelId: formData?.slackChannelId ?? null,
      dayOfWeek: formData?.dayOfWeek ?? null,
      timeOfDay: formData?.timeOfDay ?? null,
      emailMessage: formData?.emailMessage || null,
      emailSendPreviewTo: formData?.emailSendPreviewTo || null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: 'bom-packaging', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || BOM_PACKAGING_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        reportType: reportType,
        search: debouncedSearch || null,
        phasedStatuses: selectedPhasedStatuses && selectedPhasedStatuses.length > 0 ? selectedPhasedStatuses : null,
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'bom-packaging', ...payload })).unwrap();
      await dispatch(getReportSendHistory('bom-packaging')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        reportType: reportType,
        search: debouncedSearch || null,
        phasedStatuses: selectedPhasedStatuses && selectedPhasedStatuses.length > 0 ? selectedPhasedStatuses : null,
        scheduleType: formData?.type || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'bom-packaging', ...payload })).unwrap();
      await dispatch(getReportSendHistory('bom-packaging')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const handleReportTypeChange = (newType) => {
    setReportType(newType);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleExportCSV = async () => {
    const params = new URLSearchParams({
      reportType: reportType,
    });
    if (debouncedSearch) {
      params.append('search', debouncedSearch);
    }
    if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
      params.append('phasedStatuses', selectedPhasedStatuses.join(','));
    }
    const response = await fetch(`/api/v1/reports/${BOM_PACKAGING}/export?${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `${BOM_PACKAGING}-${reportType}-report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  const renderPhasedStatusOption = (phase) => (
    <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
  );

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search SKU, Product Family, Component, or Supplier"
          className="w-40 sm:w-[250px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
      <div className="relative w-40">
        <FilterDropdown
          options={PHASED_STATUSES.filter((status) => status !== PLANNED)}
          selectedOptions={selectedPhasedStatuses}
          selectTitleClassname="whitespace-nowrap"
          onChange={(value) => {
            setSelectedPhasedStatuses(value);
            setCurrentPage(1);
          }}
          placeholder="Product Status"
          renderOption={renderPhasedStatusOption}
          disableScrollRecalculation={true}
        />
      </div>
      <SelectDropdown
        selectClassname="!bg-white !w-48"
        selectTitleClassname="whitespace-nowrap"
        options={[
          { label: 'Product', value: 'product' },
          { label: 'Packaging', value: 'packaging' },
          { label: 'Product + Packaging', value: 'all' },
        ]}
        value={reportType}
        onChange={handleReportTypeChange}
      />
      <div className="flex items-center">
        <Cog8ToothIcon className="h-5 w-5 text-primary cursor-pointer" onClick={() => setIsOpenSettings(true)} />
      </div>
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${BOM_PACKAGING}-${reportType}-report`}
      onExport={handleExportCSV}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} results
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1 || isLoading}
            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages || isLoading}
            className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;
    const getStatusColor = (status) => {
        switch(status) {
            case "Active": 
                return 'bg-green-100 text-green-800'
            case "Inactive":
                return 'bg-gray-100 text-gray-800'
            case "Sunset":
                return 'bg-yellow-100 text-yellow-800'
            case "Planned":
                return 'bg-blue-100 text-blue-800'
            default:
                return 'bg-gray-100 text-gray-800'
        }
    }

  const renderTableRows = () => {
    return (reportData || []).map((row, index) => (
      <tr key={`${row?.sku || 'row'}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.sku || ''}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.product_family || ''}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.component || ''}
        </td>
        {(reportType === 'product' || reportType === 'all') && (
          <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
            {row.component_type || ''}
          </td>
        )}
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.supplier || ''}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.qty_per_unit || 0}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          {row.unit_cost || '$0.00'}
        </td>
        <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${index % 2 === 0 ? 'bg-white' : '!bg-gray-50'}`}>
          <div className={`text-xs rounded w-18 border-gray-300 text-center py-1 px-2 ${getStatusColor(row.status || '')}`}>
            {row.status || ''}
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <ReportTemplate
        reportId={BOM_PACKAGING}
        description="Bill of Materials report showing product and packaging components"
        breadcrumbs={breadcrumbs}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBackToReports}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={reportData}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No BOM data found."
        cronType={emailSettings?.cronType || null}
      >
        {renderTableRows()}
      </ReportTemplate>

      <ReportEmailSettingsSidebar
        reportType={'bom-packaging'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="bom-packaging"
      />
    </>
  );
};

export default BOMPackaging;
